<script setup lang="ts">
import LoadingButton from "@/components/LoadingButton.vue"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import { defineTranslations } from "@/utils/defineTranslations"
import { injectMandatory, useHttpClient, useLoadingState } from "@/vf"
import { computed, ref } from "vue"
import { TaskViewMode } from "../../Issue/util"

const props = defineProps<{
    registrationId: string
    team: ChampionshipRegistrationTeam
}>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const http = useHttpClient()
const viewMode = injectMandatory(TaskViewMode, "Missing mandatory injection: TaskViewMode")
const saving = useLoadingState()

const numberOfParticipants = ref(props.team.numberOfParticipants ?? 0)
const numberOfAlternates = ref(props.team.numberOfAlternates ?? 0)
const numberOfEntourages = ref(props.team.numberOfEntourages ?? 0)

const category = computed(() => props.team.championshipCategory)

/*-------------------------------------*
 *  Warnings (non-blocking)            *
 *-------------------------------------*/
const participantsWarning = computed(() => {
    if (!category.value) return null

    const min = category.value.criteriaMinStarters ?? 0
    const max = category.value.criteriaMaxStarters ?? 0
    const value = numberOfParticipants.value ?? 0

    if (value < min) {
        return trans("warning_below_min", { min })
    }
    if (value > max) {
        return trans("warning_above_max", { max })
    }
    return null
})

const alternatesWarning = computed(() => {
    if (!category.value) return null

    const max = category.value.criteriaMaxReplacements ?? 0
    const value = numberOfAlternates.value ?? 0

    if (value > max) {
        return trans("warning_above_max", { max })
    }
    return null
})

const entouragesWarning = computed(() => {
    if (!category.value) return null

    const max = category.value.criteriaMaxEntourages ?? 0
    const value = numberOfEntourages.value ?? 0

    if (value > max) {
        return trans("warning_above_max", { max })
    }
    return null
})

/*-------------------------------------*
 *  Input handling                     *
 *-------------------------------------*/
function onIntegerKeydown(e: KeyboardEvent) {
    if (e.ctrlKey || e.metaKey) {
        return
    }

    const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Enter",
        "Escape",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
    ]
    if (allowedKeys.includes(e.key)) {
        return
    }

    if (/^[0-9]$/.test(e.key)) {
        return
    }

    e.preventDefault()
}

/*-------------------------------------*
 *  Save                               *
 *-------------------------------------*/
async function save() {
    await saving.while(async () => {
        await http.put(
            `/championship/registration/${props.registrationId}/team/${props.team.id}/finalize-members-count?viewMode=${viewMode}`,
            {
                numberOfParticipants: numberOfParticipants.value,
                numberOfAlternates: numberOfAlternates.value,
                numberOfEntourages: numberOfEntourages.value,
            },
        )
        emit("updated")
    })
}

const trans = defineTranslations({
    min_max_hint: {
        de: "Min: {min}, Max: {max}",
        en: "Min: {min}, Max: {max}",
    },
    max_hint: {
        de: "Max: {max}",
        en: "Max: {max}",
    },
    warning_below_min: {
        de: "Wert liegt unter dem Minimum ({min})",
        en: "Value is below minimum ({min})",
    },
    warning_above_max: {
        de: "Wert liegt über dem Maximum ({max})",
        en: "Value exceeds maximum ({max})",
    },
    save: {
        de: "Speichern",
        en: "Save",
    },
})
</script>

<template>
    <form @submit.prevent="save()" class="p-3">
        <div class="mb-3">
            <label for="numberOfParticipants" class="font-weight-bold">
                {{ $t("@tasks:tasks.championship_registration.wizard.number_of_participants") }}
            </label>
            <input
                type="number"
                id="numberOfParticipants"
                class="form-control"
                v-model.number="numberOfParticipants"
                min="0"
                step="1"
                inputmode="numeric"
                pattern="[0-9]*"
                @keydown="onIntegerKeydown"
            />
            <small class="text-muted">
                {{
                    trans("min_max_hint", {
                        min: category?.criteriaMinStarters ?? 0,
                        max: category?.criteriaMaxStarters ?? 0,
                    })
                }}
            </small>
            <div v-if="participantsWarning" class="text-warning">
                <i class="fas fa-exclamation-triangle mr-1"></i>
                {{ participantsWarning }}
            </div>
        </div>

        <div class="mb-3">
            <label for="numberOfAlternates" class="font-weight-bold">
                {{ $t("@tasks:tasks.championship_registration.wizard.number_of_alternates") }}
            </label>
            <input
                type="number"
                id="numberOfAlternates"
                class="form-control"
                v-model.number="numberOfAlternates"
                min="0"
                step="1"
                inputmode="numeric"
                pattern="[0-9]*"
                @keydown="onIntegerKeydown"
            />
            <small class="text-muted">
                {{ trans("max_hint", { max: category?.criteriaMaxReplacements ?? 0 }) }}
            </small>
            <div v-if="alternatesWarning" class="text-warning">
                <i class="fas fa-exclamation-triangle mr-1"></i>
                {{ alternatesWarning }}
            </div>
        </div>

        <div class="mb-3">
            <label for="numberOfEntourages" class="font-weight-bold">
                {{ $t("@tasks:tasks.championship_registration.wizard.number_of_entourages") }}
            </label>
            <input
                type="number"
                id="numberOfEntourages"
                class="form-control"
                v-model.number="numberOfEntourages"
                min="0"
                step="1"
                inputmode="numeric"
                pattern="[0-9]*"
                @keydown="onIntegerKeydown"
            />
            <small class="text-muted">
                {{ trans("max_hint", { max: category?.criteriaMaxEntourages ?? 0 }) }}
            </small>
            <div v-if="entouragesWarning" class="text-warning">
                <i class="fas fa-exclamation-triangle mr-1"></i>
                {{ entouragesWarning }}
            </div>
        </div>

        <div class="d-flex justify-content-end mt-4">
            <LoadingButton class="btn btn-primary" type="submit" :loading="saving">
                {{ trans("save") }}
            </LoadingButton>
        </div>
    </form>
</template>
